import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Zap, CheckCircle, ArrowRight, Infinity } from "lucide-react";
import { startPayfastCheckout } from "@/lib/payfastCheckout";
import {
    BILLING_PLANS,
    TOKEN_PACKS,
    TOKEN_PACK_PRICES_ZAR,
    billingPath,
    formatZar,
    type TokenPackKey,
} from "@/lib/billingPlans";

interface UpgradeModalProps {
    isOpen: boolean;
    onClose: () => void;
    limitType: "mt" | "ai";
    currentTier?: string; // 'free' | 'standard' | 'premium'
}

type SubscriptionPlan = "standard" | "premium";

const TIERS = {
    free: {
        name: "Free",
        mt_chars: 50000,
        mt_chars_daily: 5000,
        ai_queries: 100,
        price: "Free",
        badge: null,
    },
    standard: {
        name: "Standard",
        mt_chars: 200000,
        mt_chars_daily: 15000,
        ai_queries: 500,
        price: `${formatZar(BILLING_PLANS[1].priceZar)}/month`,
        badge: null,
    },
    premium: {
        name: "Premium",
        mt_chars: 500000,
        mt_chars_daily: 50000,
        ai_queries: 3000,
        price: `${formatZar(BILLING_PLANS[2].priceZar)}/month`,
        badge: BILLING_PLANS[2].badge,
    },
};

function formatNumber(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return `${n}`;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({
    isOpen,
    onClose,
    limitType,
    currentTier = "free",
}) => {
    const [pendingKey, setPendingKey] = useState<string | null>(null);
    const [checkoutError, setCheckoutError] = useState<string | null>(null);
    const navigate = useNavigate();

    if (!isOpen) return null;

    // Plan changes happen on the Plan & billing page (/billing), where the plan can be
    // reviewed before continuing to payment.
    const handleSubscribe = (plan: SubscriptionPlan) => {
        onClose();
        navigate(billingPath(plan));
    };

    const handlePurchasePack = async (pack: TokenPackKey) => {
        setCheckoutError(null);
        setPendingKey(pack);
        try {
            await startPayfastCheckout("purchase-pack", { pack });
        } catch (err: any) {
            setCheckoutError(
                err.message ||
                    "Something went wrong starting checkout. Please try again.",
            );
            setPendingKey(null);
        }
    };

    const body =
        limitType === "mt"
            ? "You have used your machine translation allowance for this period."
            : "You have used your AI query allowance for this period.";

    const isFree = currentTier === "free";
    const isStandard = currentTier === "standard";
    const isPremium = currentTier === "premium";

    const showUpgradeTiers = isFree || isStandard;
    const showTokenPacks = isStandard || isPremium;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Card */}
            <div className="relative z-10 w-full max-w-xl rounded-2xl border border-slate-700/60 bg-slate-900/90 backdrop-blur-md shadow-2xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-700/50">
                    <div className="flex items-center gap-2">
                        <Zap className="w-5 h-5 text-gold-400" />
                        <h2 className="text-lg font-semibold text-white">
                            Limit Reached
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-white transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 space-y-6">
                    {checkoutError && (
                        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                            <p className="text-red-400 text-xs">
                                {checkoutError}
                            </p>
                        </div>
                    )}

                    {/* Message */}
                    <div className="p-4 rounded-lg bg-slate-800/60 border border-slate-700/50">
                        <p className="text-slate-300 text-sm">{body}</p>
                        <p className="text-slate-400 text-xs mt-1.5">
                            {isFree && "Upgrade to unlock higher limits."}
                            {isStandard &&
                                "Upgrade to Premium or purchase extra tokens."}
                            {isPremium &&
                                "Purchase extra tokens to continue immediately."}
                        </p>
                    </div>

                    {/* Tier upgrade cards (Free / Standard) */}
                    {showUpgradeTiers && (
                        <div>
                            <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-1.5">
                                <ArrowRight className="w-4 h-4 text-gold-400" />
                                Upgrade Your Plan
                            </h3>
                            <div className="grid grid-cols-2 gap-3">
                                {/* Show all tiers the user can upgrade TO */}
                                {(isFree
                                    ? ["standard", "premium"]
                                    : ["premium"]
                                ).map((tierKey) => {
                                    const tier =
                                        TIERS[tierKey as keyof typeof TIERS];
                                    const isPremiumTier = tierKey === "premium";
                                    return (
                                        <div
                                            key={tierKey}
                                            className={`rounded-xl border p-4 ${
                                                isPremiumTier
                                                    ? "border-gold-500/40 bg-gold-500/5"
                                                    : "border-slate-600 bg-slate-800/60"
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-3">
                                                <span
                                                    className={`text-xs font-medium uppercase tracking-wide ${
                                                        isPremiumTier
                                                            ? "text-gold-400"
                                                            : "text-slate-400"
                                                    }`}
                                                >
                                                    {tier.name}
                                                </span>
                                                {tier.badge && (
                                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gold-500/20 text-gold-400">
                                                        {tier.badge}
                                                    </span>
                                                )}
                                            </div>
                                            <ul className="space-y-2 text-sm text-slate-300 mb-4">
                                                <li className="flex items-center gap-2">
                                                    <CheckCircle className="w-3.5 h-3.5 text-gold-400 flex-shrink-0" />
                                                    <span>
                                                        {formatNumber(
                                                            tier.mt_chars,
                                                        )}{" "}
                                                        MT chars/month
                                                    </span>
                                                </li>
                                                <li className="flex items-center gap-2">
                                                    <CheckCircle className="w-3.5 h-3.5 text-gold-400 flex-shrink-0" />
                                                    <span>
                                                        {formatNumber(
                                                            tier.mt_chars_daily,
                                                        )}{" "}
                                                        MT chars/day
                                                    </span>
                                                </li>
                                                <li className="flex items-center gap-2">
                                                    <CheckCircle className="w-3.5 h-3.5 text-gold-400 flex-shrink-0" />
                                                    <span>
                                                        {formatNumber(
                                                            tier.ai_queries,
                                                        )}{" "}
                                                        AI queries/month
                                                    </span>
                                                </li>
                                            </ul>
                                            <p className="text-xl font-bold text-white mb-3">
                                                {tier.price}
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleSubscribe(
                                                        tierKey as SubscriptionPlan,
                                                    )
                                                }
                                                className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                                    isPremiumTier
                                                        ? "bg-gold-500 hover:bg-gold-400 text-slate-900"
                                                        : "bg-slate-700 hover:bg-slate-600 text-white"
                                                }`}
                                            >
                                                <Zap className="w-3.5 h-3.5" />
                                                Upgrade
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Token packs (Standard + Premium) */}
                    {showTokenPacks && (
                        <div>
                            <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-1.5">
                                <Infinity className="w-4 h-4 text-gold-400" />
                                Extra Token Packs
                                <span className="text-xs font-normal text-slate-400 ml-1">
                                    — one-time purchase, never expire
                                </span>
                            </h3>
                            <div className="space-y-2">
                                {TOKEN_PACKS.map((pack) => (
                                    <div
                                        key={pack.key}
                                        className="flex items-center justify-between rounded-xl border border-slate-700 bg-slate-800/60 p-4"
                                    >
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-semibold text-white">
                                                    {pack.name} Pack
                                                </span>
                                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-700 text-slate-400">
                                                    one-time
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-400 mt-0.5">
                                                {formatNumber(pack.chars)} extra
                                                MT characters
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm font-bold text-white">
                                                {formatZar(TOKEN_PACK_PRICES_ZAR[pack.key])}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handlePurchasePack(
                                                        pack.key,
                                                    )
                                                }
                                                disabled={
                                                    pendingKey === pack.key
                                                }
                                                className="px-3 py-1.5 rounded-lg bg-gold-500 hover:bg-gold-400 text-slate-900 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {pendingKey === pack.key
                                                    ? "..."
                                                    : "Buy"}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                                Extra tokens are consumed after your daily quota
                                is exhausted. They never expire and roll over
                                indefinitely. Processing will resume
                                automatically once tokens are available.
                            </p>
                        </div>
                    )}

                    {/* Dismiss */}
                    <button
                        onClick={onClose}
                        className="w-full py-2.5 rounded-lg border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 text-sm transition-colors"
                    >
                        Maybe Later
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UpgradeModal;
